/*
 * <hero-slideshow>: autoplay with segmented progress bars, pause/play, swipe, arrow keys.
 * Pauses on focus, while the tab is hidden, and never autoplays with reduced motion.
 */
(() => {
  'use strict';
  if (customElements.get('hero-slideshow')) return;

  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.slide')];
      this.bars = [...this.querySelectorAll('[data-bar]')];
      this.pauseBtn = this.querySelector('[data-pause]');
      if (this.slides.length < 2) return;

      this.duration = Number(this.dataset.interval) || 6500;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = this.reduced || !this.hasAttribute('data-autoplay');
      this.focusPaused = false;
      this.current = 0;
      this.elapsed = 0;
      this.startedAt = performance.now();

      this.bars.forEach((bar, i) => {
        bar.setAttribute('aria-label', bar.textContent.trim() + ': ' + (this.slides[i].dataset.slideName || ''));
        bar.addEventListener('click', () => this.show(i));
      });
      this.pauseBtn?.addEventListener('click', () => { this.userPaused = !this.userPaused; this.syncPlaying(); });
      this.addEventListener('focusin', () => { this.focusPaused = true; this.syncPlaying(); });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) { this.focusPaused = false; this.syncPlaying(); } });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.show(this.current + 1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.show(this.current - 1, true); }
      });
      this.onVisibility = () => this.syncPlaying();
      document.addEventListener('visibilitychange', this.onVisibility);

      // Swipe: horizontal drags only, so vertical page scroll is untouched.
      this.addEventListener('pointerdown', (e) => { this.sx = e.clientX; this.sy = e.clientY; });
      this.addEventListener('pointerup', (e) => {
        if (this.sx == null) return;
        const dx = e.clientX - this.sx, dy = e.clientY - this.sy;
        this.sx = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.show(this.current + (dx < 0 ? 1 : -1));
      });

      // Theme editor: show the selected slide and hold it there.
      this.addEventListener('shopify:block:select', (e) => { this.editing = true; this.syncPlaying(); this.show(this.slides.indexOf(e.target)); });
      this.addEventListener('shopify:block:deselect', () => { this.editing = false; this.syncPlaying(); });

      // Later slides' images wait until the page (and slide 1) has loaded.
      const loadRest = () => this.slides.forEach((s) => this.loadSlide(s));
      if (document.readyState === 'complete') setTimeout(loadRest, 1500);
      else window.addEventListener('load', () => setTimeout(loadRest, 1500), { once: true });

      this.show(0, false, false);
      this.syncPlaying();
      this.raf = requestAnimationFrame((t) => this.tick(t));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    get playing() {
      return !this.userPaused && !this.focusPaused && !this.editing && !document.hidden;
    }

    syncPlaying() {
      const now = performance.now();
      if (this.wasPlaying && !this.playing) this.elapsed += now - this.startedAt;
      if (!this.wasPlaying && this.playing) this.startedAt = now;
      this.wasPlaying = this.playing;
      // Live region only while stopped, so autoplay is not announced every few seconds.
      this.setAttribute('aria-live', this.playing ? 'off' : 'polite');
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-label', this.userPaused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
      this.pauseBtn.querySelector('[data-icon-pause]').hidden = this.userPaused;
      this.pauseBtn.querySelector('[data-icon-play]').hidden = !this.userPaused;
    }

    loadSlide(slide) {
      slide?.querySelectorAll('[data-srcset], [data-src]').forEach((el) => {
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
        if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
      });
    }

    show(index, focusBar = false, warm = true) {
      const n = this.slides.length;
      const next = ((index % n) + n) % n;
      this.loadSlide(this.slides[next]);
      if (warm) this.loadSlide(this.slides[(next + 1) % n]); // warm the one after
      this.slides.forEach((slide, i) => {
        const on = i === next;
        slide.classList.toggle('is-on', on);
        slide.toggleAttribute('inert', !on);
        slide.setAttribute('aria-hidden', String(!on));
      });
      this.bars.forEach((bar, i) => {
        bar.classList.toggle('is-done', i < next);
        bar.querySelector('i').style.transform = i < next ? 'scaleX(1)' : 'scaleX(0)';
        bar.setAttribute('aria-current', String(i === next));
      });
      this.current = next;
      this.elapsed = 0;
      this.startedAt = performance.now();
      if (focusBar) this.bars[next]?.focus();
    }

    tick(now) {
      if (this.playing) {
        const progress = Math.min(1, (this.elapsed + now - this.startedAt) / this.duration);
        const bar = this.bars[this.current]?.querySelector('i');
        if (bar) bar.style.transform = `scaleX(${progress})`;
        if (progress >= 1) this.show(this.current + 1);
      }
      this.raf = requestAnimationFrame((t) => this.tick(t));
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
})();
